import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Input, Button, Dropdown, Empty, Radio, message } from 'antd';
import dayjs from 'dayjs';
import {
  ArrowDown, ArrowUp, PhoneMissed, PhoneOff, Play, Pause, UserX, ChevronDown, Download, StickyNote,
} from 'lucide-react';
import {
  AGENT_STATUS, CHANNEL_LABEL, KIND_LABEL, OUTCOME_LABEL, callCenterApi, callsOf, careOf, customerByPhone, fmtDuration,
  useCallCenter, type AgentStatus, type CallKind, type CallLog,
} from '../callCenterStore';
import { meName } from '../softphone';
import { CALL_LOG_API, fetchRecording, type Recording } from '../callLogApi';

/**
 * Đóng việc gọi lại: bắt buộc nhập kết quả xử lý (ai đóng, đã làm gì) để còn dấu vết trên hành trình.
 * Khiếu nại: hỏi thêm "Khách đã hài lòng chưa?" — chưa hài lòng thì mở lại và chuyển quản lý.
 */
export function CompleteCallbackButton({ id, complaint, children, buttonProps }: {
  id: string; complaint?: boolean; children?: React.ReactNode; buttonProps?: React.ComponentProps<typeof Button>;
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [satisfied, setSatisfied] = useState<boolean | null>(null);
  const quick = complaint
    ? ['Đã gọi xin lỗi, khách đồng ý hướng xử lý', 'Đã hẹn khách quay lại xử lý miễn phí', 'Đã hoàn tiền / giảm giá cho khách']
    : ['Đã nhắn Zalo cho khách', 'Khách báo không còn nhu cầu', 'Đã xử lý trực tiếp tại quầy'];
  const done = () => {
    if (complaint && satisfied === null) { message.warning('Chọn khách đã hài lòng hay chưa'); return; }
    if (!callCenterApi.completeCallback(id, text, meName(), complaint ? { satisfied: !!satisfied } : {})) { message.warning('Nhập kết quả xử lý'); return; }
    message.success(!complaint ? 'Đã hoàn thành việc gọi lại' : satisfied ? 'Đã đóng khiếu nại' : 'Khách chưa hài lòng — đã mở lại khiếu nại và chuyển quản lý');
    setOpen(false);
    setText('');
    setSatisfied(null);
  };
  return (
    <>
      <Button size="small" {...buttonProps} onClick={() => setOpen(true)}>{children || 'Xong'}</Button>
      <Modal open={open} title={complaint ? 'Đóng khiếu nại' : 'Hoàn thành việc gọi lại'} onCancel={() => setOpen(false)} width={480} zIndex={1050}
        footer={[<Button key="c" onClick={() => setOpen(false)}>Huỷ</Button>, <Button key="s" type="primary" disabled={!text.trim() || (complaint && satisfied === null)} onClick={done}>Xác nhận</Button>]}>
        <div className="space-y-2">
          <div className="text-sm text-gray-600 dark:text-gray-300">Kết quả xử lý <span className="text-rose-500">*</span></div>
          <Input.TextArea autoFocus value={text} onChange={e => setText(e.target.value)} autoSize={{ minRows: 3, maxRows: 6 }}
            placeholder={complaint ? 'Đã xử lý khiếu nại thế nào, khách phản hồi ra sao...' : 'Đã liên hệ khách bằng cách nào, kết quả ra sao...'} />
          <div className="flex flex-wrap gap-1.5">
            {quick.map(q => (
              <button key={q} type="button" onClick={() => setText(q)} className="text-xs px-2 py-1 rounded-full border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-[#3e91ab] cursor-pointer">{q}</button>
            ))}
          </div>
          {complaint && (
            <div className="pt-2">
              <div className="text-sm text-gray-600 dark:text-gray-300 mb-1.5">Khách đã hài lòng với cách xử lý? <span className="text-rose-500">*</span></div>
              <Radio.Group value={satisfied} onChange={e => setSatisfied(e.target.value)} optionType="button" buttonStyle="solid"
                options={[{ value: true, label: 'Đã hài lòng — đóng' }, { value: false, label: 'Chưa hài lòng — chuyển quản lý' }]} />
              {satisfied === false && <div className="text-xs text-rose-600 mt-1.5">Khiếu nại này đóng lại và tạo khiếu nại mới (mở lại) giao Quản lý chi nhánh xử lý.</div>}
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}

export const escapeHtml =(s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
export const textToHtml = (s: string) => s.trim().split(/\n+/).map(l => `<p>${escapeHtml(l)}</p>`).join('');
export const htmlToPlain = (h: string) =>
  h.replace(/<br\s*\/?>/gi, '\n').replace(/<\/p>/gi, '\n').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&').trim();

const KIND_STYLE: Record<CallKind, { cls: string; Icon: React.ComponentType<{ size?: number | string }> }> = {
  in: { cls: 'text-blue-600 bg-blue-50 border-blue-100', Icon: ArrowDown },
  out: { cls: 'text-emerald-600 bg-emerald-50 border-emerald-100', Icon: ArrowUp },
  missed_in: { cls: 'text-red-600 bg-red-50 border-red-100', Icon: PhoneMissed },
  missed_out: { cls: 'text-orange-600 bg-orange-50 border-orange-100', Icon: PhoneOff },
};

export function KindBadge({ kind }: { kind: CallKind }) {
  const { cls, Icon } = KIND_STYLE[kind];
  return (
    <span className={`px-2 py-1 rounded text-[11px] font-medium border inline-flex items-center gap-1 w-max ${cls}`}>
      <Icon size={12} /> {KIND_LABEL[kind]}
    </span>
  );
}

export function UnknownBadge() {
  return (
    <span className="px-2 py-1 rounded text-[11px] font-medium border inline-flex items-center gap-1 w-max text-gray-600 bg-gray-100 border-gray-200">
      <UserX size={12} /> Số máy lạ
    </span>
  );
}

export function AgentStatusDropdown() {
  const { agentStatus } = useCallCenter();
  const meta = AGENT_STATUS[agentStatus];
  return (
    <Dropdown
      trigger={['click']}
      menu={{
        selectedKeys: [agentStatus],
        items: (Object.keys(AGENT_STATUS) as AgentStatus[]).map(k => ({
          key: k,
          label: <span className="flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${AGENT_STATUS[k].dot}`} />{AGENT_STATUS[k].label}</span>,
        })),
        onClick: ({ key }) => {
          callCenterApi.setAgentStatus(key as AgentStatus);
          message.success(`Đã chuyển trạng thái: ${AGENT_STATUS[key as AgentStatus].label}`);
        },
      }}
    >
      <button type="button" className="h-9 px-3 rounded-lg border border-gray-200 bg-white dark:bg-gray-900 dark:border-gray-700 text-sm flex items-center gap-2 hover:border-[#3e91ab] cursor-pointer" title="Trạng thái nhận cuộc gọi của bạn">
        <span className={`w-2.5 h-2.5 rounded-full ${meta.dot}`} />
        <span className="font-medium text-gray-700 dark:text-gray-200">{meta.label}</span>
        <ChevronDown size={14} className="text-gray-400" />
      </button>
    </Dropdown>
  );
}

/** Ghi âm thật lấy từ máy chủ (cuộc gọi đã đồng bộ, có serverId). CareSoft chưa xử lý xong file → cho thử lại */
function ServerRecording({ serverId, compact }: { serverId: number; compact: boolean }) {
  const [state, setState] = useState<{ loading: boolean; rec?: Recording; msg?: string }>({ loading: true });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let alive = true;
    setState({ loading: true });
    fetchRecording(serverId).then(r => { if (alive) setState({ loading: false, rec: r.rec, msg: r.msg || 'Không lấy được ghi âm' }); });
    return () => { alive = false; };
  }, [serverId, attempt]);
  const box = `flex items-center gap-3 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 ${compact ? 'px-3 py-1.5' : 'px-4 py-2.5'}`;
  if (state.loading) return <div className={`${box} text-xs text-gray-500`}>Đang lấy ghi âm…</div>;
  if (!state.rec) {
    return (
      <div className={`${box} text-xs text-gray-500 justify-between`}>
        <span>{state.msg}</span>
        <button type="button" onClick={() => setAttempt(a => a + 1)} className="font-semibold text-[#3e91ab] hover:underline cursor-pointer shrink-0">Thử lại</button>
      </div>
    );
  }
  return (
    <div className={box}>
      <audio controls preload="metadata" src={state.rec.url} className="flex-1 min-w-0 h-8" />
      {state.rec.download_url && (
        <a href={state.rec.download_url} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-gray-600" title="Tải xuống ghi âm">
          <Download size={14} />
        </a>
      )}
    </div>
  );
}

/** Trình phát ghi âm: cuộc gọi đã lưu lên máy chủ thì phát file thật; còn lại mô phỏng thanh tiến trình theo thời lượng */
export function RecordingPlayer({ call, compact = false }: { call: CallLog; compact?: boolean }) {
  if (CALL_LOG_API && call.serverId) return <ServerRecording serverId={call.serverId} compact={compact} />;
  return <MockRecording call={call} compact={compact} />;
}

function MockRecording({ call, compact }: { call: CallLog; compact: boolean }) {
  const [pos, setPos] = useState(0);
  const [playing, setPlaying] = useState(false);
  useEffect(() => {
    if (!playing) return;
    const t = setInterval(() => setPos(p => {
      if (p + 1 >= call.duration) { setPlaying(false); return call.duration; }
      return p + 1;
    }), 1000);
    return () => clearInterval(t);
  }, [playing, call.duration]);
  const pct = call.duration ? (pos / call.duration) * 100 : 0;
  return (
    <div className={`flex items-center gap-3 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 ${compact ? 'px-3 py-1.5' : 'px-4 py-2.5'}`}>
      <button type="button" onClick={() => { if (pos >= call.duration) setPos(0); setPlaying(p => !p); }}
        className="w-8 h-8 shrink-0 rounded-full border border-gray-200 flex items-center justify-center text-gray-600 hover:text-[#3e91ab] hover:border-[#3e91ab] cursor-pointer" title={playing ? 'Tạm dừng' : 'Phát ghi âm'}>
        {playing ? <Pause size={13} /> : <Play size={13} className="pl-0.5" />}
      </button>
      <span className="text-xs text-gray-500 font-medium tabular-nums min-w-[78px]">{fmtDuration(pos)} / {fmtDuration(call.duration)}</span>
      <div className="flex-1 h-1.5 bg-gray-200 rounded-full cursor-pointer relative" title="Tua"
        onClick={e => {
          const r = e.currentTarget.getBoundingClientRect();
          setPos(Math.round(((e.clientX - r.left) / r.width) * call.duration));
        }}>
        <div className="h-full bg-[#3e91ab] rounded-full" style={{ width: `${pct}%` }} />
      </div>
      <button type="button" onClick={() => message.info('Bản ghi âm sẽ tải được khi nối API tổng đài')} className="text-gray-400 hover:text-gray-600 cursor-pointer" title="Tải xuống ghi âm">
        <Download size={14} />
      </button>
    </div>
  );
}

export function RecordingModal({ call, onClose }: { call: CallLog | null; onClose: () => void }) {
  const s = useCallCenter();
  const c = call ? customerByPhone(s, call.phone) : null;
  return (
    <Modal open={!!call} onCancel={onClose} footer={null} title="Ghi âm cuộc gọi" destroyOnClose width={460}>
      {call && (
        <div className="space-y-3">
          <div className="text-sm text-gray-600 dark:text-gray-300">
            <div><b className="text-gray-900 dark:text-gray-100">{c?.name || 'Số máy lạ'}</b> · {call.phone}</div>
            <div className="text-xs text-gray-400 mt-0.5">{dayjs(call.startedAt).format('HH:mm DD/MM/YYYY')} · NV: {call.agent}</div>
          </div>
          <RecordingPlayer call={call} />
          {call.note && <div className="text-sm bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-100 dark:border-yellow-900 rounded p-3 whitespace-pre-line">{call.note}</div>}
        </div>
      )}
    </Modal>
  );
}

/** Xem nhanh ghi chú gần nhất của 1 số + thêm ghi chú mới */
export function QuickNotesModal({ phone, onClose, onOpenDetail }: { phone: string | null; onClose: () => void; onOpenDetail?: () => void }) {
  const s = useCallCenter();
  const [text, setText] = useState('');
  const customer = phone ? customerByPhone(s, phone) : null;
  const phones = useMemo(() => (customer ? customer.phones : phone ? [phone] : []), [customer, phone]);
  const items = useMemo(() => [
    ...careOf(s, phones).map(e => ({ id: e.id, at: e.at, who: e.author, tag: CHANNEL_LABEL[e.channel], text: htmlToPlain(e.content) })),
    ...callsOf(s, phones).filter(c => c.note).map(c => ({ id: c.id, at: c.startedAt, who: c.agent, tag: `Cuộc gọi · ${OUTCOME_LABEL[c.outcome]}`, text: c.note! })),
  ].sort((a, b) => b.at - a.at).slice(0, 6), [s, phones]);

  useEffect(() => { setText(''); }, [phone]);

  const save = () => {
    if (!phone || !text.trim()) return;
    callCenterApi.addCare(phone, 'note', textToHtml(text), meName());
    setText('');
    message.success('Đã thêm ghi chú');
  };

  return (
    <Modal open={!!phone} onCancel={onClose} footer={null} width={480}
      title={<span className="flex items-center gap-2"><StickyNote size={16} className="text-[#3e91ab]" /> Ghi chú · {customer?.name || phone}</span>}>
      <div className="space-y-3">
        <Input.TextArea value={text} onChange={e => setText(e.target.value)} autoSize={{ minRows: 2, maxRows: 5 }} placeholder="Thêm ghi chú nhanh cho khách này..."
          onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) save(); }} />
        <div className="flex justify-between items-center">
          {onOpenDetail ? <button type="button" onClick={onOpenDetail} className="text-xs text-[#3e91ab] hover:underline cursor-pointer">Mở hồ sơ khách hàng →</button> : <span />}
          <Button type="primary" size="small" disabled={!text.trim()} onClick={save}>Lưu ghi chú</Button>
        </div>
        <div className="max-h-[320px] overflow-y-auto space-y-2 pt-1">
          {items.length ? items.map(it => (
            <div key={it.id} className="bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-lg p-3">
              <div className="flex justify-between text-[11px] text-gray-400 mb-1 gap-2">
                <span><b className="text-gray-600 dark:text-gray-300">{it.who}</b> · {it.tag}</span>
                <span className="shrink-0">{dayjs(it.at).format('HH:mm DD/MM/YYYY')}</span>
              </div>
              <div className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-line">{it.text}</div>
            </div>
          )) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có ghi chú" />}
        </div>
      </div>
    </Modal>
  );
}
